import { useEffect, useState } from "react";
import { useDiscoveryRequest } from "../../../../contexts/DiscoveryRequestContext";
import { Button } from "../../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../design/dialog";
import { Input } from "../../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../../design/fieldset";
import { Textarea } from "../../../../design/textarea";
import { Select } from "../../../../design/select";
import { PlusIcon } from "@heroicons/react/20/solid";
import { toLocalDateString } from "../../../../utils/timeString";
import { useFile } from "../../../../contexts/FileContext";
import { useNavigate } from "react-router-dom";
import { sideTypes, SideType } from "../../../../models/SideType";

interface Props {
  children?: React.ReactNode;
}

interface Errors {
  title?: string;
  description?: string;
}

const CreateDiscoveryRequestButton: React.FC<Props> = ({ children }) => {
  const { createDiscoveryRequest } = useDiscoveryRequest();
  const { createFile } = useFile();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [side, setSide] = useState<SideType>("");
  const [dueDate, setDueDate] = useState<string>(
    toLocalDateString(new Date()).slice(0, 10),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const navigate = useNavigate();

  useEffect(() => {
    const now = new Date();
    setDueDate(toLocalDateString(now).slice(0, 10));
  }, [isOpen]); // Empty dependency array to run only once on mount

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!title) {
      newErrors.title = "Title is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    try {
      const discoveryRequestId = await createDiscoveryRequest({
        title,
        description,
        side,
        dueDate: new Date(`${dueDate}T00:00:00Z`),
        status: "open",
        discoveryRequestTasks: [],
      });
      if (file) {
        await createFile(file, {
          type: "discovery",
          name: file.name,
          size: file.size,
          mimeType: file.type,
          description: `File for discovery request: ${title}`,
          status: "uploading",
          discoveryRequest: discoveryRequestId,
        });
      }
      navigate(`/discovery-request/${discoveryRequestId}`);
      clearForm();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setErrors({});
    setIsOpen(false);
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        {children ?? (
          <div className="flex items-center gap-2">
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:block">Create Discovery Request</span>
          </div>
        )}
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Create Discovery Request</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>File</Label>
                <Input
                  type="file"
                  onChange={(e) =>
                    setFile(e.target.files ? e.target.files[0] : null)
                  }
                />
              </Field>
              <Field>
                <Label>Side</Label>
                <Select
                  value={side}
                  onChange={(e) => setSide(e.target.value as SideType)}
                >
                  <option value="" disabled>
                    Select side
                  </option>
                  {Object.entries(sideTypes).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                <Label htmlFor="dueDate">Due Date</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  disabled={isSaving}
                />
              </Field>
              <Field>
                <Label>Title</Label>
                <Input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="E.g. Initial Discovery Request"
                />
                {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description"
                  rows={3}
                />
                {errors.description && (
                  <ErrorMessage>{errors.description}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateDiscoveryRequestButton;
